import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tag, Typography } from 'antd';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';

const SLIDER_MAGNITUDE_EXPLANATION = String.raw`
The magnitude score checks how closely each instrument's slider level matches
its benchmark conviction for the period.

### 1. Put both values on the slider scale

PM calibrates the instrument's **PCS — Period Conviction Score** onto the same
$-5$ to $+5$ scale as the user's slider. It then uses the last slider value
set by the user for that instrument in the period. If the slider was never moved, its
value is $0$.

### 2. Score the distance

For instrument $i$, let $s_i$ be the user's slider and $b_i$ the calibrated
benchmark PCS:

$$
c_i=\max\!\left(0,\min\!\left(1,1-\frac{|s_i-b_i|}{5}\right)\right).
$$

- **Exact match:** $100\%$ credit.
- **One slider point away:** $80\%$ credit.
- **Three slider points away:** $40\%$ credit.
- **Five or more points away:** no credit.

Despite the "Slider magnitude" label, PM compares the **signed slider levels**, not
only their absolute magnitudes. A $+4$ benchmark and a $-4$ slider are eight
points apart and receive no magnitude credit.

### 3. Stronger benchmark views weight more

Each instrument is weighted by the absolute size of its calibrated PCS:

$$
C_p=\frac{\sum_i |b_i|c_i}{\sum_i |b_i|}.
$$

A benchmark of $\pm4$ therefore has four times the influence of a benchmark of
$\pm1$. A zero benchmark has no magnitude weight; neutral or conflicted views
are assessed separately by Restraint when unclear. If every benchmark weight is
zero, the magnitude score defaults to full credit.

### 4. Convert the score to Pillar 1 points

$$
\mathrm{MagnitudePoints}_p=25C_p.
$$

For example, suppose one instrument scores $0.80$ with weight $4$, and another
scores $0.60$ with weight $1$:

$$
C_p=\frac{4(0.80)+1(0.60)}{4+1}=0.76
$$

$$
\mathrm{MagnitudePoints}=25(0.76)=19.
$$

The complete Pillar 1 score is calculated for each period, then the period
scores are averaged.
`;

const PillarOneSliderMagnitudeGuide: React.FC = () => (
  <aside
    aria-labelledby="pillar-one-slider-magnitude-title"
    className="assessment-inspect-assessment__pillar-one-guide"
  >
    <header className="assessment-inspect-assessment__pillar-one-guide-header">
      <div className="assessment-inspect-assessment__pillar-one-guide-kicker">
        <span>
          <InfoCircleOutlined aria-hidden="true" /> Scoring guide
        </span>
        <Tag color="blue">25% of Pillar 1</Tag>
      </div>
      <Typography.Title id="pillar-one-slider-magnitude-title" level={4}>
        Slider magnitude score
      </Typography.Title>
      <Typography.Text type="secondary">
        How each period’s slider levels become up to 25 points.
      </Typography.Text>
    </header>

    <div className="assessment-inspect-assessment__pillar-one-guide-body">
      <ReactMarkdown rehypePlugins={[rehypeKatex]} remarkPlugins={[remarkMath]}>
        {SLIDER_MAGNITUDE_EXPLANATION}
      </ReactMarkdown>
    </div>
  </aside>
);

export default PillarOneSliderMagnitudeGuide;
